import { Flex, HStack, Icon, Text, Textarea, VStack } from '@chakra-ui/react'
import { Clock } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DropdownSelect } from '~/components/shared/Dropdown/DropdownSelect'
import { Dropzone } from '~/components/shared/Dropzone'
import { TagInput } from '~/components/shared/TagInput'
import { ContactSearchInput } from '~/components/specialized/ContactSearchInput/ContactSearchInput'
import { FaxAttachmentConfigMultifile } from '~/constants/fileAttachmentConfig'
import { useNewFaxFormStyle } from '~/pages/Home/Modals/NewFaxModal/componentStyles/NewFaxForm.style'
import { useGetAllPhoneNumbers } from '~/queries/phoneNumberQueries'
import { Spacing } from '~/types/ThemeTypes'
import { isValidEmail } from '~/utils/common'
import { formatPhoneNumber } from '~/utils/formatter'

import { Field, Toggle } from '@mangovoice/mango-ui'
import { ContactSourceType, PhoneNumber } from '@mangovoice/types'

import { type NewFax, useNewFaxViewModel } from './viewModels'

// Module-level so the reference is stable and doesn't defeat ContactSearchInput's memo.
const CONTACT_SOURCE_TYPE_FILTER = [ContactSourceType.Extensions]

interface NewFaxMenuProps {
  selectedFrom: PhoneNumber | null
  to: string
  isToInvalid: boolean
  sendEmailConfirmation: boolean
  emailConfirmations: string[]
  includeCoverPage: boolean
  coverPageFile: File | null
  coverPageNote: string
  setCoverPageNote: (note: string) => void
  setIncludeCoverPage: (state: boolean) => void
  onToChanged: (to: string) => void
  onFromSelected: (from: PhoneNumber | null) => void
  onFileSelectionChanged: (files: File[]) => void
  onEmailConfirmationToggled: (enabled: boolean) => void
  onEmailConfirmationsChanged: (emails: string[]) => void
}

const maxEmailConfirmations = 5
export const NewFaxForm = ({
  selectedFrom,
  to,
  isToInvalid,
  sendEmailConfirmation,
  emailConfirmations,
  includeCoverPage,
  coverPageFile,
  coverPageNote,
  setCoverPageNote,
  setIncludeCoverPage,
  onToChanged,
  onFromSelected,
  onFileSelectionChanged,
  onEmailConfirmationToggled,
  onEmailConfirmationsChanged,
}: NewFaxMenuProps) => {
  const styles = useNewFaxFormStyle()
  const {
    state: { lastUsedFaxNumber },
  } = useNewFaxViewModel()
  const { results: allPhoneNumbers } = useGetAllPhoneNumbers()
  const { t } = useTranslation()

  const fromList = allPhoneNumbers
    .filter((phoneNumber) => phoneNumber.id !== lastUsedFaxNumber?.id)
    .map((phoneNumber) => phoneNumber as NewFax.FaxListPhoneNumber)
  if (fromList && lastUsedFaxNumber) {
    fromList.unshift({ ...lastUsedFaxNumber, isRecent: true })
  }

  const handleDropAccepted = (files: File[]) => {
    if (!files.length) {
      return
    }

    onFileSelectionChanged(files)
  }

  const handleDropzoneCleared = () => {
    onFileSelectionChanged([])
  }

  const handleEmailConfirmationToggled = (details: { checked: boolean }) => {
    onEmailConfirmationToggled(details.checked)
  }

  const handleTagsUpdated = (tags: string[]) => {
    onEmailConfirmationsChanged(tags)
  }

  const numbersJsx = useMemo(() => {
    return fromList.map((phoneNumber) => {
      const formattedPhoneNumber = formatPhoneNumber(phoneNumber.number)
      return {
        value: phoneNumber.id,
        content: (
          <HStack key={phoneNumber.id} p="0.5rem">
            {phoneNumber.isRecent && (
              <Icon>
                <Clock size={16} />
              </Icon>
            )}
            <Text css={styles.phoneNameText}>{phoneNumber.name}</Text>
            <Text
              css={styles.phoneNumberText}
            >{` - ${formattedPhoneNumber}`}</Text>
          </HStack>
        ),
      }
    })
  }, [fromList])

  const handleSelectedInternal = (id: number, index: number) => {
    const selectedFrom = fromList[index]
    onFromSelected(selectedFrom)
  }

  const handleToChangedInternal = (newToValue: string) => {
    onToChanged(newToValue)
  }

  const getSelectedFromPhoneNumberIndex = (from: PhoneNumber | null) => {
    if (!from) {
      return
    }

    const index = fromList.findIndex((phoneNumber) => {
      return phoneNumber.id === from.id
    })

    return index
  }

  const selectedPhoneNumberIndex = useMemo(() => {
    return getSelectedFromPhoneNumberIndex(selectedFrom)
  }, [selectedFrom, fromList])

  return (
    <VStack align="stretch" gap={Spacing._2}>
      <DropdownSelect
        placeholder={t('newFaxForm.dropdownPlaceholder')}
        items={numbersJsx}
        selectedIndex={selectedPhoneNumberIndex}
        onSelect={handleSelectedInternal}
      />
      <ContactSearchInput
        isInvalid={isToInvalid}
        value={to}
        onValueChanged={handleToChangedInternal}
        contactSourceTypeFilter={CONTACT_SOURCE_TYPE_FILTER}
      />
      <VStack align="stretch" gap={Spacing._4}>
        <Toggle
          checked={includeCoverPage}
          onCheckedChange={(details: { checked: boolean }) =>
            setIncludeCoverPage(details.checked)
          }
        >
          <Text>{t('newFaxForm.switchText.includeCoverPage')}</Text>
        </Toggle>
        {includeCoverPage && (
          <Field.Root>
            <Field.Label>
              {t('newFaxForm.coverPageNoteLabel', 'Cover page note')}
            </Field.Label>
            <Textarea
              placeholder={t('newFaxForm.textAreaPlaceholder')}
              value={coverPageNote}
              onChange={(e) => setCoverPageNote(e.target.value)}
            />
          </Field.Root>
        )}
      </VStack>
      <Dropzone
        coverPage={coverPageFile}
        handleCoverPageDelete={() => setIncludeCoverPage(false)}
        acceptedFileTypes={FaxAttachmentConfigMultifile.AcceptedFileTypes}
        allowMultiple={FaxAttachmentConfigMultifile.MultipleFiles}
        dropzoneProps={{
          onDropAccepted: handleDropAccepted,
        }}
        maxFileSize={FaxAttachmentConfigMultifile.MaximumFileSize}
        maxFileSizePlaceholder={t('newFaxForm.dropzonePlaceholder')}
        onClear={handleDropzoneCleared}
        onFileChange={handleDropAccepted}
      />
      <Flex align="stretch" height={Spacing._2} />
      <VStack align="stretch" gap={Spacing._4}>
        <Toggle
          checked={sendEmailConfirmation}
          onCheckedChange={handleEmailConfirmationToggled}
        >
          <Text>{t('newFaxForm.switchText.sendEmailConfirmation')}</Text>
        </Toggle>
        {sendEmailConfirmation && (
          <TagInput
            label={t(
              'newFaxForm.emailConfirmationsLabel',
              'Confirmation emails',
            )}
            placeholder={t('newFaxForm.tagInputPlaceholder')}
            guideText={t('newFaxForm.tagInputGuideText', {
              maxEmailConfirmations,
            })}
            maxTags={maxEmailConfirmations}
            valueValidator={isValidEmail}
            valueType={t('newFaxForm.emailValueType', 'email')}
            initialTags={emailConfirmations}
            onTagsUpdated={handleTagsUpdated}
          />
        )}
      </VStack>
    </VStack>
  )
}
